import type { AdminStatistics, StatisticsPeriodKey, ThresholdedCount } from '@/admin/adminApi';

import { dayLabel, ENTRY_KINDS } from './dashboardModel';

/**
 * Regles de presentation des statistiques d'usage — module **pur**, sans React.
 *
 * Tout ce que l'ecran decide de dire y est : periode, lecture d'un compteur
 * passe par le seuil, libelles de semaine, phrases d'alternative textuelle. Le
 * rendu ne fait que les placer.
 *
 * **Le seuil n'est jamais applique ici** : il l'est par le serveur, qui ne rend
 * aucun nombre sous `k`. Ce module ne fait que **ne pas inventer** ce que le
 * serveur a retenu — un compteur supprime ne se lit jamais comme un nombre, ni
 * comme zero, ni comme un tiret : il se lit « < k ».
 */

// ---------------------------------------------------------------------------
// Periode
// ---------------------------------------------------------------------------

export const DEFAULT_STATISTICS_PERIOD: StatisticsPeriodKey = '90d';

/** Periodes **predefinies** : le serveur refuse toute autre valeur (422), l'ecran n'en propose pas. */
export const STATISTICS_PERIODS: readonly { readonly key: StatisticsPeriodKey; readonly label: string }[] = [
  { key: '30d', label: '30 jours' },
  { key: '90d', label: '90 jours' },
];

/**
 * Une valeur inconnue (ou absente) retombe sur le defaut : jamais d'erreur d'URL,
 * et jamais une valeur libre envoyee au serveur.
 */
export const parseStatisticsPeriod = (raw: string | null): StatisticsPeriodKey =>
  STATISTICS_PERIODS.find((period) => period.key === raw)?.key ?? DEFAULT_STATISTICS_PERIOD;

// ---------------------------------------------------------------------------
// Compteurs soumis au seuil
// ---------------------------------------------------------------------------

/**
 * Le nombre a afficher, ou `null` quand le compteur est supprime.
 *
 * `suppressed` fait foi : un serveur qui rendrait `{ suppressed: true, value: 7 }`
 * ne ferait pas ecrire 7.
 */
export const readCount = (count: ThresholdedCount): number | null => (count.suppressed ? null : count.value);

/** Espace insecable : « < » ne reste jamais seul en bout de ligne. */
const NBSP = ' ';

/** Ce qui s'ecrit a la place d'un nombre supprime, avec le `k` que le serveur rend. */
export const suppressedLabel = (k: number): string => `<${NBSP}${k.toLocaleString('fr-FR')}`;

export const countLabel = (count: ThresholdedCount, k: number): string => {
  const value = readCount(count);

  return value === null ? suppressedLabel(k) : value.toLocaleString('fr-FR');
};

/** Vrai quand au moins un compteur de la reponse est lisible : sinon l'ecran n'a rien a montrer. */
export const hasReadableValue = (statistics: AdminStatistics): boolean =>
  [
    statistics.active.day,
    statistics.active.week,
    statistics.active.month,
    ...statistics.weekly.flatMap((week) => [week.newAccounts, ...ENTRY_KINDS.map(({ kind }) => week.entriesByKind[kind])]),
  ].some((count) => readCount(count) !== null);

// ---------------------------------------------------------------------------
// Semaines
// ---------------------------------------------------------------------------

export type StatisticsWeek = AdminStatistics['weekly'][number];

/**
 * « 12 oct. », et « 12 oct. (en cours) » pour la semaine en cours : le repere
 * est ecrit, il ne repose pas sur une couleur (WCAG 1.4.1) et se retrouve dans
 * la table masquee du graphique.
 */
export const weekLabel = (week: StatisticsWeek): string => `${dayLabel(week.weekStart)}${week.inProgress ? ' (en cours)' : ''}`;

/** « 5 semaines, de 14 sept. à 12 oct. (en cours). » */
export const describeWeeks = (weeks: readonly StatisticsWeek[]): string =>
  weeks.length === 0
    ? 'Aucune semaine.'
    : `${weeks.length.toLocaleString('fr-FR')} semaines, de ${weekLabel(weeks[0]!)} à ${weekLabel(weeks[weeks.length - 1]!)}.`;

/**
 * Phrase d'une serie hebdomadaire. Elle dit combien de semaines sont affichees,
 * la derniere valeur lisible, et que **les autres sont sous le seuil** — jamais
 * « absentes » : une valeur supprimee n'est pas un manque de donnees.
 */
export const describeWeeklySeries = (
  name: string,
  weeks: readonly StatisticsWeek[],
  values: readonly (number | null)[],
  k: number,
): string => {
  const shown = weeks.flatMap((_, index) => (values[index] === null || values[index] === undefined ? [] : [index]));

  if (shown.length === 0) {
    return `${name} : aucune semaine n’atteint le seuil (${suppressedLabel(k)}).`;
  }

  const last = shown[shown.length - 1]!;
  const hidden = weeks.length - shown.length;
  const rest = hidden > 0 ? `, ${hidden.toLocaleString('fr-FR')} sous le seuil (${suppressedLabel(k)})` : '';
  const plural = shown.length > 1 ? 's' : '';

  return `${name} : ${shown.length.toLocaleString('fr-FR')} semaine${plural} affichée${plural}${rest} ; dernière valeur affichée ${values[last]!.toLocaleString('fr-FR')} (${weekLabel(weeks[last]!)}).`;
};

/** Valeurs d'une serie : le nombre lisible, ou `null` — que le graphique ne dessine pas. */
export const newAccountsValues = (weeks: readonly StatisticsWeek[]): readonly (number | null)[] =>
  weeks.map((week) => readCount(week.newAccounts));

export const entriesSeries = (
  weeks: readonly StatisticsWeek[],
): readonly { readonly label: string; readonly values: readonly (number | null)[] }[] =>
  ENTRY_KINDS.map(({ kind, label }) => ({ label, values: weeks.map((week) => readCount(week.entriesByKind[kind])) }));
